import { useParams, Link } from 'react-router-dom'

import { Button } from '@/common/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import PageContainer from '@/common/components/PageContainer'
import NavigateBackButton from '@/common/components/NavigateBackButton'
import { useTablePagination } from '@/common/hooks/useTablePaginate'
import { ArrowLeft, History } from 'lucide-react'

import useQueryGetUsageHistory from '@/modules/batches/api/hooks/useQueryGetUsageHistory'
import UsageHistoryTable from '@/modules/batches/components/UsageHistoryTable'

export default function UsageHistoryPage() {
	const { couponId } = useParams<{ couponId: string }>()
	const { pageSize, currentPage } = useTablePagination()

	const payload = {
		couponId: couponId!,
		take: pageSize,
		skip: (currentPage - 1) * pageSize,
	}

	const { data, isLoading, error } = useQueryGetUsageHistory(payload)

	if (error) {
		return (
			<PageContainer>
				<div className="flex h-64 items-center justify-center">
					<div className="text-center">
						<p className="mb-4 text-red-500">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
						<Button asChild variant="outline">
							<Link to="/operator/coupons">
								<ArrowLeft className="mr-2 h-4 w-4" />
								กลับไปหน้าคูปอง
							</Link>
						</Button>
					</div>
				</div>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<div className="space-y-1.5">
						<CardTitle className="flex items-center gap-2">
							<History className="h-5 w-5" />
							ประวัติการใช้งานคูปอง
						</CardTitle>
						<p className="text-sm text-muted-foreground">
							รหัสคูปอง: <span className="font-mono">{couponId}</span>
						</p>
					</div>
					<NavigateBackButton />
				</CardHeader>
				<CardContent>
					<UsageHistoryTable
						data={data?.data || []}
						total={data?.total || 0}
						isLoading={isLoading}
					/>
				</CardContent>
			</Card>

			{/* Summary Card */}
			{data && data.data.length > 0 && (
				<Card className="mt-6">
					<CardHeader>
						<CardTitle>สรุปการใช้งาน</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 gap-4 md:grid-cols-3">
							<div className="rounded-lg bg-muted p-4 text-center">
								<p className="text-2xl font-bold text-primary">{data.total}</p>
								<p className="text-sm text-muted-foreground">
									จำนวนการใช้งานทั้งหมด
								</p>
							</div>
							<div className="rounded-lg bg-muted p-4 text-center">
								<p className="text-2xl font-bold text-green-600">
									{new Set(data.data.map((item) => item.playerId)).size}
								</p>
								<p className="text-sm text-muted-foreground">
									จำนวนผู้เล่นที่ใช้งาน
								</p>
							</div>
							<div className="rounded-lg bg-muted p-4 text-center">
								<p className="text-2xl font-bold text-blue-600">
									{data.data.length > 0
										? new Date(
												data.data[data.data.length - 1].usedAt,
											).toLocaleDateString('th-TH')
										: '-'}
								</p>
								<p className="text-sm text-muted-foreground">การใช้งานล่าสุด</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</PageContainer>
	)
}
